import {
  BRIDGE_SERVICE_TYPE,
  type BridgeDiscovery,
  type DiscoveredBridge,
  type DiscoveryState,
  decodeBridgeTxt,
} from '@kinoq/camera-protocol';

/** Bridges added by address and requests with a body; both shells implement it. A pinned
 *  bridge survives discovery losing it until forgetBridge. */
export interface ShellBridges {
  /** Rejects with "No KinoQ bridge at <host>:<port>" or "needs a newer bridge". */
  addBridge(o: { host: string; port: number }): Promise<{ id: string; name: string }>;
  forgetBridge(o: { id: string }): Promise<void>;
  /** Error statuses resolve. An unknown or unreachable bridge rejects on the tablet and
   *  resolves the shell's 404 or 502 on the desktop. */
  bridgeRequest(o: {
    id: string;
    method: 'PUT';
    path: string;
    body: string;
  }): Promise<{ status: number; body: string }>;
}

/** `window.rosaDesktop`, exposed by the Electron shell's preload (desktop/src/api.ts). */
export interface DesktopApi {
  version: 1;
  // Optional: a desktop app older than manual bridges lacks it.
  bridges?: ShellBridges;
  discovery: {
    start(opts: { serviceType: string }): Promise<void>;
    stop(): Promise<void>;
    addListener(
      name: 'found' | 'lost' | 'error',
      handler: (e: never) => void,
    ): Promise<{ remove(): Promise<void> }>;
  };
}

/** `window.rosaTablet`, built by installTabletApi inside the Capacitor Android shell. */
export interface TabletApi {
  version: 1;
  discovery: DesktopApi['discovery'];
  bridges?: ShellBridges;
  googleIdToken(): Promise<string>;
}

interface FoundEvent {
  host: string;
  port: number;
  txt: Record<string, string>;
}

export function desktopApi(): DesktopApi | undefined {
  const api = (window as { rosaDesktop?: DesktopApi }).rosaDesktop;
  return api?.version === 1 ? api : undefined;
}

export function shellApi(): DesktopApi | TabletApi | undefined {
  const w = window as { rosaDesktop?: DesktopApi; rosaTablet?: TabletApi };
  if (w.rosaDesktop?.version === 1) return w.rosaDesktop;
  return w.rosaTablet?.version === 1 ? w.rosaTablet : undefined;
}

/** The desktop shell forwards rosa-bridge:// to the bridge's LAN address; the tablet's page proxies /_bridge/ itself. */
export function bridgeUrl(bridgeId: string): string {
  return (window as { rosaDesktop?: DesktopApi }).rosaDesktop
    ? `rosa-bridge://${bridgeId}`
    : `${location.origin}/_bridge/${bridgeId}`;
}

const shared = new WeakMap<DesktopApi | TabletApi, DesktopDiscovery>();

export function discoveryFor(api: DesktopApi | TabletApi): DesktopDiscovery {
  let discovery = shared.get(api);
  if (!discovery) {
    discovery = new DesktopDiscovery(api.discovery);
    shared.set(api, discovery);
  }
  return discovery;
}

/** Ported from KinoQ's NsdDiscovery: the preload API has the same shape as its NsdPlugin. */
export class DesktopDiscovery implements BridgeDiscovery {
  private found = new Map<string, DiscoveredBridge>();
  private error: string | undefined;
  private state: DiscoveryState = { bridges: [] };
  private listeners = new Set<() => void>();
  private removals: (() => Promise<void>)[] = [];

  // Bumped by every start/stop; a sequence that no longer owns it abandons its work.
  private generation = 0;

  // One browse serves every owner, so only the last release tears it down.
  private refs = 0;

  // Calls into main run one at a time in issue order, so a start never overtakes a stop.
  private queue: Promise<void> = Promise.resolve();

  constructor(private plugin: DesktopApi['discovery']) {}

  start(): void {
    // An error means the browse failed to start, so the next owner must browse again.
    if (++this.refs > 1 && this.error === undefined) return;
    const generation = ++this.generation;
    const dead = this.removals;
    this.removals = [];
    if (this.error !== undefined) {
      this.error = undefined;
      this.publish();
    }
    this.queue = this.queue
      .then(async () => {
        for (const remove of dead) await remove();
        await this.begin(generation);
      })
      .catch((cause: unknown) => this.fail(generation, cause));
  }

  stop(): void {
    if (this.refs === 0 || --this.refs > 0) return;
    const generation = ++this.generation;
    const removals = this.removals;
    this.removals = [];
    this.found.clear();
    this.error = undefined;
    this.publish();
    this.queue = this.queue
      .then(async () => {
        for (const remove of removals) await remove();
        await this.plugin.stop();
      })
      .catch((cause: unknown) => this.fail(generation, cause));
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): DiscoveryState {
    return this.state;
  }

  private async begin(generation: number): Promise<void> {
    const removals: (() => Promise<void>)[] = [];
    const add = async (
      name: 'found' | 'lost' | 'error',
      handler: (data: never) => void,
    ): Promise<boolean> => {
      removals.push((await this.plugin.addListener(name, handler)).remove);
      return generation === this.generation;
    };
    const live =
      (await add('found', (e: FoundEvent) => this.onFound(generation, e))) &&
      (await add('lost', (e: { id: string }) => this.onLost(generation, e.id))) &&
      (await add('error', (e: { message: string }) => this.onError(generation, e.message)));
    if (!live) {
      for (const remove of removals) await remove();
      return;
    }
    this.removals.push(...removals);
    await this.plugin.start({ serviceType: BRIDGE_SERVICE_TYPE });
  }

  private fail(generation: number, cause: unknown): void {
    if (generation !== this.generation) return;
    this.error = cause instanceof Error ? cause.message : String(cause);
    this.publish();
  }

  private onFound(generation: number, e: FoundEvent): void {
    if (generation !== this.generation) return;
    const txt = decodeBridgeTxt(e.txt);
    if (!txt) return;
    this.found.set(txt.id, { ...txt, host: e.host, port: e.port });
    this.publish();
  }

  private onLost(generation: number, id: string): void {
    if (generation !== this.generation) return;
    if (!this.found.delete(id)) return;
    this.publish();
  }

  private onError(generation: number, message: string): void {
    if (generation !== this.generation) return;
    this.error = message;
    this.publish();
  }

  private publish(): void {
    const bridges = [...this.found.values()].sort((a, b) => a.name.localeCompare(b.name));
    this.state = this.error === undefined ? { bridges } : { bridges, error: this.error };
    for (const l of [...this.listeners]) l();
  }
}
